import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

// ── Enums ─────────────────────────────────────────────────────────────────

export type GuidanceMode = "Planned" | "ActiveReroute" | "DestinationOnly" | "Completed";
export type StopVerificationStatus = "VerifiedInRadius" | "VerifiedOutsideRadius" | "Unverified";
export type CustomerResolutionStatus =
  | "PendingOtp"
  | "Delivered"
  | "ProvisionalDelivered"
  | "PendingReconciliation"
  | "NotDelivered"
  | "CustomerNoShow"
  | "DeliveryFailed"
  | "ReturnedToDispatch"
  | "ReturnDiscrepancy";
export type OtpProvenance = "Online" | "OfflineProvisional";
export type NavigationEventType =
  | "OffRoute"
  | "Rerouted"
  | "RerouteCapReached"
  | "DestinationOnlyModeActivated";

// ── DTOs ──────────────────────────────────────────────────────────────────

export interface StopConfirmationEvidenceDto {
  id: string;
  tripRouteSnapshotStopId: string;
  confirmedByUserId: string;
  confirmedByName: string;
  confirmedAt: string;
  latitude?: number;
  longitude?: number;
  gpsAccuracyMetres?: number;
  speedKmh?: number;
  distanceFromStopMetres?: number;
  verificationStatus: StopVerificationStatus;
  reason?: string;
  wasOffline: boolean;
}

export interface CustomerStopResolutionDto {
  id: string;
  tripRouteSnapshotStopId: string;
  orderId: string;
  customerId: string;
  customerName: string;
  status: CustomerResolutionStatus;
  otpProvenance?: OtpProvenance;
  otpAcceptedAt?: string;
  offlineSyncValidationPassed?: boolean;
  noShowReason?: string;
  proofAttachmentUrl?: string;
  reconciliationStartedAt?: string;
  reconciledByUserId?: string;
  reconciledAt?: string;
  reconciliationNotes?: string;
}

export interface TripSnapshotStopDto {
  id: string;
  tripRouteSnapshotId?: string;
  routeStopId?: string;
  stopName: string;
  sequence: number;
  latitude?: number;
  longitude?: number;
  isCompleted: boolean;
  completedAt?: string;
  confirmations: StopConfirmationEvidenceDto[];
  customerResolutions: CustomerStopResolutionDto[];
}

export interface TripNavigationEventDto {
  id: string;
  tripRouteSnapshotId: string;
  eventType: NavigationEventType;
  latitude?: number;
  longitude?: number;
  gpsAccuracyMetres?: number;
  speedKmh?: number;
  offRouteDistanceMetres?: number;
  offRouteDurationSeconds?: number;
  rerouteCountAfterEvent?: number;
  occurredAt: string;
}

export interface TripRouteSnapshotDto {
  id: string;
  dispatchRecordId: string;
  routeTemplateId?: string | null;
  routeName: string;
  stopOrderPolicy: string;
  guidanceMode: GuidanceMode;
  autoRerouteCount: number;
  scheduleOverrideReason?: string;
  stops: TripSnapshotStopDto[];
  navigationEvents: TripNavigationEventDto[];
}

export interface ReconciliationItemDto {
  resolutionId: string;
  orderId: string;
  customerId: string;
  customerName: string;
  stopName: string;
  dispatchRecordId: string;
  status: CustomerResolutionStatus;
  otpProvenance?: OtpProvenance;
  reconciliationStartedAt?: string;
  orderNumber: string;
  dispatchNumber: string;
  tripRouteSnapshotId: string;
  driverName?: string;
  vehicleNumber?: string;
  failureKind?: "NoShow" | "DeliveryFailed";
  failureReason?: string;
  driverNotes?: string;
  driverReportedUndeliveredQuantity: number;
  returnStatus?: "Pending" | "Confirmed" | "Discrepancy";
  lines: DeliveryReturnReconciliationLineDto[];
}

export interface DeliveryReturnReconciliationLineDto {
  dispatchRecordItemId: string;
  orderItemId: string;
  productName: string;
  variety?: string;
  plannedQuantity: number;
  quantityDispatched: number;
  driverReportedUndeliveredQuantity: number;
  physicallyReturnedQuantity?: number;
  varianceQuantity?: number;
}

export interface ReconciliationListResultDto {
  items: ReconciliationItemDto[];
  totalCount: number;
}

// ── Request Types ─────────────────────────────────────────────────────────

export interface AssignRouteToTripRequest {
  dispatchRecordId: string;
  routeTemplateId: string;
  stopOrderOverrideReason?: string;
}

export interface SubmitStopConfirmationRequest {
  tripRouteSnapshotStopId: string;
  latitude?: number;
  longitude?: number;
  gpsAccuracyMetres?: number;
  speedKmh?: number;
  wasOffline?: boolean;
  reason?: string;
}

export interface ConfirmOtpDeliveryRequest {
  resolutionId: string;
  otpToken: string;
  wasOffline?: boolean;
}

export interface RecordCustomerNoShowRequest {
  resolutionId: string;
  reason?: string;
}

export interface SyncOfflineOtpRequest {
  resolutionId: string;
  otpToken: string;
}

export interface RecordNavigationEventRequest {
  tripRouteSnapshotId: string;
  eventType: NavigationEventType;
  latitude?: number;
  longitude?: number;
  gpsAccuracyMetres?: number;
  speedKmh?: number;
  offRouteDistanceMetres?: number;
  offRouteDurationSeconds?: number;
}

export interface ReconcileDeliveryRequest {
  resolutionId: string;
  confirmDelivered: boolean;
  notes?: string;
}

export interface ConfirmDeliveryReturnRequest {
  customerStopResolutionId: string;
  lines: Array<{ dispatchRecordItemId: string; physicallyReturnedQuantity: number }>;
  notes?: string;
}

// ── Normalizers ───────────────────────────────────────────────────────────

const normalizeStopConfirmation = (raw: any): StopConfirmationEvidenceDto => ({
  id: raw.id,
  tripRouteSnapshotStopId: raw.tripRouteSnapshotStopId,
  confirmedByUserId: raw.confirmedByUserId,
  confirmedByName: raw.confirmedByName,
  confirmedAt: raw.confirmedAt,
  latitude: raw.latitude ?? raw.confirmationLatitude,
  longitude: raw.longitude ?? raw.confirmationLongitude,
  gpsAccuracyMetres: raw.gpsAccuracyMetres,
  speedKmh: raw.speedKmh,
  distanceFromStopMetres: raw.distanceFromStopMetres,
  verificationStatus: raw.verificationStatus,
  reason: raw.reason,
  wasOffline: raw.wasOffline ?? false,
});

const normalizeCustomerResolution = (raw: any): CustomerStopResolutionDto => ({
  id: raw.id,
  tripRouteSnapshotStopId: raw.tripRouteSnapshotStopId,
  orderId: raw.orderId,
  customerId: raw.customerId,
  customerName: raw.customerName,
  status: raw.status,
  otpProvenance: raw.otpProvenance,
  otpAcceptedAt: raw.otpAcceptedAt,
  offlineSyncValidationPassed: raw.offlineSyncValidationPassed,
  noShowReason: raw.noShowReason,
  proofAttachmentUrl: raw.proofAttachmentUrl,
  reconciliationStartedAt: raw.reconciliationStartedAt,
  reconciledByUserId: raw.reconciledByUserId,
  reconciledAt: raw.reconciledAt,
  reconciliationNotes: raw.reconciliationNotes,
});

const normalizeTripStop = (raw: any): TripSnapshotStopDto => ({
  id: raw.id,
  tripRouteSnapshotId: raw.tripRouteSnapshotId,
  routeStopId: raw.routeStopId,
  stopName: raw.stopName,
  sequence: raw.sequence,
  latitude: raw.latitude,
  longitude: raw.longitude,
  isCompleted: raw.isCompleted,
  completedAt: raw.completedAt,
  confirmations: raw.confirmations
    ? (raw.confirmations as any[]).map(normalizeStopConfirmation)
    : raw.latestConfirmation
      ? [normalizeStopConfirmation(raw.latestConfirmation)]
      : [],
  customerResolutions: (raw.customerResolutions ?? []).map(normalizeCustomerResolution),
});

const normalizeNavigationEvent = (raw: any): TripNavigationEventDto => ({
  id: raw.id,
  tripRouteSnapshotId: raw.tripRouteSnapshotId,
  eventType: raw.eventType,
  latitude: raw.latitude,
  longitude: raw.longitude,
  gpsAccuracyMetres: raw.gpsAccuracyMetres,
  speedKmh: raw.speedKmh,
  offRouteDistanceMetres: raw.offRouteDistanceMetres,
  offRouteDurationSeconds: raw.offRouteDurationSeconds,
  rerouteCountAfterEvent: raw.rerouteCountAfterEvent,
  occurredAt: raw.occurredAt,
});

const normalizeSnapshot = (raw: any): TripRouteSnapshotDto => ({
  id: raw.id,
  dispatchRecordId: raw.dispatchRecordId,
  routeTemplateId: raw.routeTemplateId,
  routeName: raw.routeName,
  stopOrderPolicy: raw.stopOrderPolicy,
  guidanceMode: raw.guidanceMode,
  autoRerouteCount: raw.autoRerouteCount,
  scheduleOverrideReason: raw.scheduleOverrideReason,
  stops: (raw.stops ?? []).map(normalizeTripStop),
  navigationEvents: (raw.navigationEvents ?? []).map(normalizeNavigationEvent),
});

const normalizeReconciliationItem = (raw: any): ReconciliationItemDto => ({
  resolutionId: raw.resolutionId ?? raw.customerStopResolutionId,
  orderId: raw.orderId,
  customerId: raw.customerId,
  customerName: raw.customerName,
  stopName: raw.stopName,
  dispatchRecordId: raw.dispatchRecordId ?? raw.dispatchNumber,
  status: raw.status,
  otpProvenance: raw.otpProvenance,
  reconciliationStartedAt: raw.reconciliationStartedAt,
  orderNumber: raw.orderNumber,
  dispatchNumber: raw.dispatchNumber,
  tripRouteSnapshotId: raw.tripRouteSnapshotId,
  driverName: raw.driverName,
  vehicleNumber: raw.vehicleNumber,
  failureKind: raw.failureKind,
  failureReason: raw.failureReason,
  driverNotes: raw.driverNotes,
  driverReportedUndeliveredQuantity: Number(raw.driverReportedUndeliveredQuantity ?? 0),
  returnStatus: raw.returnStatus,
  lines: (raw.lines ?? []).map((line: Record<string, unknown>) => ({
    ...line,
    plannedQuantity: Number(line.plannedQuantity ?? 0),
    quantityDispatched: Number(line.quantityDispatched ?? 0),
    driverReportedUndeliveredQuantity: Number(line.driverReportedUndeliveredQuantity ?? 0),
    physicallyReturnedQuantity: line.physicallyReturnedQuantity == null ? undefined : Number(line.physicallyReturnedQuantity),
    varianceQuantity: line.varianceQuantity == null ? undefined : Number(line.varianceQuantity),
  })),
});

export interface TripSummaryDto {
  snapshotId: string;
  hasRouteSnapshot?: boolean;
  dispatchRecordId: string;
  dispatchNumber: string;
  routeName: string;
  guidanceMode: GuidanceMode;
  stopOrderPolicy: string;
  totalStops: number;
  completedStops: number;
  expectedDeliveryDate?: string;
  dispatchedAt?: string;
  dispatchStatus: string;
  driverName?: string;
  driverUserId?: string;
  customerNames: string[];
}

export type PortalTripFilter = "All" | "Active";

// ── API ────────────────────────────────────────────────────────────────────

export const tripGuidanceApi = {
  assignRoute: async (data: AssignRouteToTripRequest): Promise<TripRouteSnapshotDto> => {
    const res = await apiClient.post<ApiResponse<TripRouteSnapshotDto>>("/trip-guidance/assign-route", data);
    return normalizeSnapshot(res.data.data);
  },

  getSnapshot: async (dispatchRecordId: string): Promise<TripRouteSnapshotDto | null> => {
    const res = await apiClient.get<ApiResponse<TripRouteSnapshotDto>>(
      `/trip-guidance/dispatch/${dispatchRecordId}/snapshot`
    );
    return res.data.data ? normalizeSnapshot(res.data.data) : null;
  },

  getNavigationEvents: async (snapshotId: string): Promise<TripNavigationEventDto[]> => {
    const res = await apiClient.get<ApiResponse<TripNavigationEventDto[]>>(
      `/trip-guidance/snapshot/${snapshotId}/events`
    );
    return (res.data.data ?? []).map(normalizeNavigationEvent);
  },

  recordNavigationEvent: async (data: RecordNavigationEventRequest): Promise<TripNavigationEventDto> => {
    const res = await apiClient.post<ApiResponse<TripNavigationEventDto>>("/trip-guidance/navigation-event", data);
    return normalizeNavigationEvent(res.data.data);
  },

  submitStopConfirmation: async (data: SubmitStopConfirmationRequest): Promise<StopConfirmationEvidenceDto> => {
    const payload = {
      tripRouteSnapshotStopId: data.tripRouteSnapshotStopId,
      latitude: data.latitude,
      longitude: data.longitude,
      gpsAccuracyMetres: data.gpsAccuracyMetres,
      speedKmh: data.speedKmh,
      reason: data.reason,
      wasOffline: data.wasOffline ?? false,
    };
    const res = await apiClient.post<ApiResponse<StopConfirmationEvidenceDto>>("/trip-guidance/stop-confirmation", payload);
    return normalizeStopConfirmation(res.data.data);
  },

  confirmOtpDelivery: async (data: ConfirmOtpDeliveryRequest): Promise<CustomerStopResolutionDto> => {
    const payload = {
      customerStopResolutionId: data.resolutionId,
      otpToken: data.otpToken,
      wasOffline: data.wasOffline ?? false,
    };
    const res = await apiClient.post<ApiResponse<CustomerStopResolutionDto>>("/trip-guidance/otp-confirm", payload);
    return normalizeCustomerResolution(res.data.data);
  },

  recordNoShow: async (data: RecordCustomerNoShowRequest): Promise<CustomerStopResolutionDto> => {
    const payload = {
      customerStopResolutionId: data.resolutionId,
      reason: data.reason?.trim() ?? "Customer unreachable",
      proofAttachmentUrl: undefined as string | undefined,
    };
    const res = await apiClient.post<ApiResponse<CustomerStopResolutionDto>>("/trip-guidance/no-show", payload);
    return normalizeCustomerResolution(res.data.data);
  },

  syncOfflineOtp: async (data: SyncOfflineOtpRequest): Promise<CustomerStopResolutionDto> => {
    const payload = {
      customerStopResolutionId: data.resolutionId,
      otpToken: data.otpToken,
      offlineAcceptedAt: new Date().toISOString(),
    };
    const res = await apiClient.post<ApiResponse<CustomerStopResolutionDto>>("/trip-guidance/sync-offline-otp", payload);
    return normalizeCustomerResolution(res.data.data);
  },

  getReconciliationQueue: async (): Promise<ReconciliationListResultDto> => {
    const res = await apiClient.get<ApiResponse<ReconciliationListResultDto>>("/trip-guidance/reconciliation");
    const raw = res.data.data;
    return {
      items: (raw?.items ?? []).map(normalizeReconciliationItem),
      totalCount: raw?.totalCount ?? 0,
    };
  },

  reconcileDelivery: async (data: ReconcileDeliveryRequest): Promise<CustomerStopResolutionDto> => {
    const payload = {
      customerStopResolutionId: data.resolutionId,
      confirmDelivered: data.confirmDelivered,
      notes: data.notes,
    };
    const res = await apiClient.post<ApiResponse<CustomerStopResolutionDto>>("/trip-guidance/reconciliation/resolve", payload);
    return normalizeCustomerResolution(res.data.data);
  },

  confirmDeliveryReturn: async (data: ConfirmDeliveryReturnRequest): Promise<CustomerStopResolutionDto> => {
    const res = await apiClient.post<ApiResponse<CustomerStopResolutionDto>>(
      "/trip-guidance/reconciliation/confirm-return",
      data
    );
    return normalizeCustomerResolution(res.data.data);
  },

  getAllTrips: async (driverUserId?: string, filter: PortalTripFilter = "All"): Promise<TripSummaryDto[]> => {
    const res = await apiClient.get<ApiResponse<TripSummaryDto[]>>("/trip-guidance/trips", {
      params: {
        ...(driverUserId ? { driverUserId } : {}),
        ongoingOnly: filter === "Active"
      }
    });
    return res.data.data;
  },
};
